import saveAs from 'file-saver';
import { useCallback, useEffect, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getBasename, s3dirname } from '../apps/Storage/components/StorageMy';
import { ReactComponent as Filter } from '../assets/svg/filter.svg';
import { ReactComponent as Bell } from '../assets/svg/notification.svg';
import { ReactComponent as Notify } from '../assets/svg/Notify.svg';
import { useAlert } from '../components/alert';
import { extractErrorDetail, isConsumer, toMplatBackendDate, useIsAdmin, useIsMedialogistic, useIsPartnerSource } from '../components/common';
import Button from '../components/mplat/Button/Button';
import Content from '../components/mplat/Content';
import ContentShadow from '../components/mplat/ContentShadow';
import { DatePicker } from '../components/mplat/DatePicker';
import { getPrefix } from '../components/mplat/Deals/DealContentConsumer';
import MainContentFilter from '../components/mplat/MainContentFilter';
import MainHeader from '../components/mplat/MainHeader';
import { MobileDateFilterItem } from '../components/mplat/MobileDateFilterItem';
import MobileFilter from '../components/mplat/MobileFilter';
import { MobileFilterItem } from '../components/mplat/MobileFilterItem';
import { ErrorModal } from '../components/mplat/Modals/ErrorModal';
import { PermissionViewModal } from '../components/mplat/Modals/PermissionViewModal';
import MultiSelect from '../components/mplat/MultiSelect';
import { Spinner } from '../components/mplat/Spinner';
import { useLanguage, useRole } from '../hooks';
import { AccessPermissionUnit, useGetMediaHeaderMutation, useGetMetaDataMutation, useLazyGetContentUnitMediaQuery } from '../services/content_v1';
import { NotificationResponse, NotificationSystem, useLazyGetNotificationsUserQuery } from '../services/notifications';
import { BASE_URL } from '../services/reauth';
import { useLazyGetSdnReportQuery } from '../services/sdn';
import { useGetAccountListQuery } from '../services/users_v1';
import { extractAccount, extractPrefix } from './ContentEditorPage';


export enum NOTIFY_SEVERITY {
    URGENT = '_urgent',
    HIGH = '_high',
    LOW = '_low',
}

// под вопросом отображение:
// {"id":4434,"added":"2026-02-13T15:01:44.227300+03:00","template":"user.account.created","group":{"id":1,"name":"Уведомления для учетных записей пользователя"},"prio":0,"context":{"lang":"ru","activate_url":"https://mplat.tb.msk-ix.ru/activate-account?uid=MjUw&token=d3y1qv-3d6fdff20a300d789dcce5194d95dcd3"}}
// {"id":4427,"added":"2026-02-13T11:59:18.812131+03:00","template":"user.email.confirm","group":{"id":1,"name":"Уведомления для учетных записей пользователя"},"prio":0,"context":{"to":"ogure4ku@bk.ru","code":"907598","lang":"ru"}}
// {"id":4429,"added":"2026-02-13T12:01:27.013911+03:00","template":"user.registration.send","group":{"id":1,"name":"Уведомления для учетных записей пользователя"},"prio":0,"context":{"lang":"ru"}}

export type NotifificationTemplate =
    'content.access.permission.issued.recipient' | // - Предоставлено разрешение (для потребителя)
    'content.access.permission.issued.other' | // Предоставлено разрешение (для партнеров-источников и владельцев контента)
    'content.access.permission.revoked.recipient' | // Разрешение отозвано (для потребителя)
    'content.access.permission.revoked.other' | //  Разрешение отозвано (для партнеров-источников и владельцев контента)
    'content.access.permission.reminder.expire.consumer' |
    'content.access.permission.reminder.expire.provider' |
    'content.access.permission.reminder.expire.partner' |
    'content.access.permission.warn' |
    'content.access.request.media.new' |
    'content.access.request.media.granted' |
    'content.access.request.media.rejected' |
    'content.access.request.tv.new' |
    'content.access.request.tv.rejected' |
    'content.access.request.tv.forwarded' |
    'content.access.request.tv.copied' |
    'content.permission.letter' |
    'content.unit.access.request.new' |
    'content.unit.access.request.granted' |
    'content.unit.access.request.pending' |
    'content.unit.access.request.rejected' |
    'content.unit.new' |
    'content.unit.send_to_user' |
    'moderation.send_report' |
    'moderation.unit_agreed' |
    'moderation.unit.agreed' |
    'moderation.unit_filled' |
    'moderation.unit.filled' |
    'moderation.unit_changed' |
    'moderation.unit.changed' |
    'product.request.new' |
    'product.request.agreed' |
    'product.request.granted' |
    'product.request.rejected' |
    'product.new' |
    'product.offer' |
    'user.request.new' |
    'content.unit.offer' |
    'feedback.message.new' |
    'support.message.new' |
    'moderation.unit_published' |
    'moderation.unit.published' |
    'user.account.activate' |
    'user.request.existed' |
    'orbox.task.failed' |
    'orbox.check.failed' |
    'sdn.attr.orbox.checked' |
    'sdn.attr.linza.checked' |
    'sdn.attr.checksums.failed' |
    'sdn.permission.added' |
    'sdn.permission.changed' |
    'sdn.permission.revoked' |
    'sdn.disk.blocked.write' |
    'sdn.disk.unlocked.write' |
    'sdn.disk.freespace.warning' |
    'sdn.disk.blocked.access' |
    'sdn.disk.unlocked.access' |
    'sdn.object.ratelimit' |
    'sdn.iam.svcacct.invite' |
    'sdn.object.ratelimit.locked' |
    'notification.message';

export function encodePrefix(prefix?: string) {
    if (prefix?.endsWith('/')) {
        return extractAccount(prefix) + '/' + encodeURIComponent(extractPrefix(prefix));
    } else if (prefix !== undefined && prefix !== null) {
        const folderPrefix = s3dirname(prefix);
        return extractAccount(folderPrefix) + '/' + encodeURIComponent(extractPrefix(folderPrefix));
    }
    return '';
}

export function encodeKey(prefix?: string) {
    if (prefix?.endsWith('/')) {
        return encodeURIComponent(extractPrefix(prefix));
    } else if (prefix !== undefined && prefix !== null) {
        const folderPrefix = s3dirname(prefix);
        return encodeURIComponent(extractPrefix(folderPrefix));
    }
    return '';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getMessage(item: NotificationResponse<any> | NotificationSystem<any>, onDownload: any, highlight?: string, callback?: (id: number) => void): React.JSX.Element {
    const alert = useAlert();
    const lang = useLanguage();
    const isPartnerSource = useIsPartnerSource();
    const isMedialogistika = useIsMedialogistic();
    const isAdmin = useIsAdmin();

    const [fetchPdf] = useLazyGetSdnReportQuery();

    if (lang === 'ru') {
        try {
            const template = item.template as NotifificationTemplate;
            switch (template) {
                case 'content.unit.access.request.new':
                    return (<>
                        Потребитель
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context.request?.sender?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.request?.sender?.name || item.context.request?.sender?.profile?.company}
                            />
                        </Link>
                        {' '}
                        запрашивает <Link className="link-accent" to={`/deals/orders?id=${item.context.request?.id}`}>условия сделки</Link> на
                        {' '}
                        {item.context.request.content ?
                            <Link className="link-accent" to={`/content/view/${item.context.request.content.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={item.context.request.content.title}
                                />
                            </Link>
                            :
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            item.context.request.units.map((u: any) =>
                                <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={u.title}
                                    />
                                    {' '}
                                </Link>
                            )
                        }
                    </>);
                case 'content.access.request.media.new':
                    return (<>
                        Потребитель
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context?.consumer?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context?.consumer?.name}
                            />
                        </Link>
                        {' '}
                        запрашивает разрешение на контент
                        {' '}
                        {item.context?.units?.map((u: { id: number, title: string }) =>
                            <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={u.title}
                                />
                                {' '}
                            </Link>
                        )}
                    </>);
                case 'content.access.request.tv.new':
                    return (<>
                        Потребитель
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context?.consumer?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context?.consumer?.name}
                            />
                        </Link>
                        {' '}
                        запрашивает <Link className="link-accent" to={`/deals/orders?id=${item.context.id}`}>разрешение</Link> на телеканалы
                        {' '}
                        {item.context?.units?.map((u: { id: number, title: string }) =>
                            <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={u.title}
                                />
                                {' '}
                            </Link>
                        )}
                    </>);
                case 'support.message.new':
                    return (<>
                        Сообщение в поддержку: Пользователь
                        {' '}
                        <Link className="link-accent" to={`/users/${item.context.request?.sender.user_id}`}>
                            {item.context.request?.sender?.firstname} {item.context.request?.sender?.lastname} {item.context.request?.sender?.user}
                        </Link>
                        {' ('}
                        <Link className="link-accent" to={`/accounts/${item.context.request?.sender.account_id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.request?.sender.account?.name || ''}
                            />
                        </Link>
                        {') '}
                        пишет
                        "{item.context.request?.message}"
                    </>);
                case 'feedback.message.new':
                    return (<>
                        Получено новое сообщение по форме обратной связи {item.context.feedback?.sender?.email ? `${item.context.feedback?.sender?.email}` : ''}:
                        {' '}
                        <pre>"{item.context.feedback?.message}"</pre>
                    </>);
                case 'user.request.new':
                    return (<>
                        Заявка на регистрацию: <strong>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={`${item.context.profile.lastname} ${item.context.profile.firstname}`}
                            />
                        </strong> компания <strong>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.profile.company}
                            />
                        </strong>
                    </>);
                case 'content.access.request.media.granted':
                    return (
                        <>
                            Получено разрешение на скачивание
                            {' '}
                            {item.context.units?.map((u: { id: number, title: string }) => <>
                                <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={u.title}
                                    />
                                    {' '}
                                </Link>
                                <Button size="small" onClick={() => onDownload(u.id, u.title)}>
                                    <Trans>Download</Trans>
                                </Button>
                            </>
                            )}
                        </>
                    );
                case 'content.unit.access.request.granted':
                    return (
                        <>
                            Получено разрешение на скачивание
                            {' '}
                            {item.context.request.content ?
                                <Link className="link-accent" to={`/content/view/${item.context.request.content.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={item.context.request.content.title}
                                    />
                                </Link>
                                :
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.units.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.title}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                            {' '}
                            {item.context.request?.prefixes?.[0] &&
                                <Link to={`/sdn/avail/${getPrefix(item.context.request?.prefixes)}`}><Button size="small">
                                    <Trans>Download</Trans>
                                </Button></Link>}
                        </>
                    );
                case 'content.unit.access.request.pending':
                    return (
                        <>
                            Запрос на скачивание
                            {' '}
                            {item.context.request.content ?
                                <Link className="link-accent" to={`/content/view/${item.context.request.content.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={item.context.request.content.title}
                                    />
                                </Link>
                                :
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.units.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.title}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                            {' '}
                            находится на рассмотрении
                        </>
                    );
                case 'content.unit.access.request.rejected':
                    return (<>
                        Запрос на скачивание
                        {' '}
                        {item.context.request.content ?
                            <Link className="link-accent" to={`/content/view/${item.context.request.content.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={item.context.request.content.title}
                                />
                            </Link>
                            :
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            item.context.request.units.map((u: any) =>
                                <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={u.title}
                                    />
                                    {' '}
                                </Link>
                            )
                        }
                        {' '}
                        отклонен
                    </>);
                case 'content.access.request.media.rejected':
                    return (<>
                        Запрос на контент
                        {' '}
                        {item.context.units.map((u: { id: number, title: string }) => (isPartnerSource && !isMedialogistika) ?
                            `${u.title}`
                            :
                            <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={u.title}
                                />
                                {' '}
                            </Link>
                        )}
                        {' '}
                        отклонен
                    </>);
                case 'content.access.request.tv.rejected':
                    return (<>
                        Запрос на телеканалы
                        {' '}
                        {item.context.units.map((u: { id: number, title: string }) =>
                            <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={u.title}
                                />
                                {' '}
                            </Link>
                        )
                        }
                        {' '}
                        отклонен
                    </>);
                case 'content.access.request.tv.copied':
                    return (<>
                        Правообладатель {item.context.additional?.sendby?.account?.org?.name || item.context.additional?.sendby?.account?.name} отправил копию <Link className="link-accent" to={`/deals/orders?id=${item.context.id}`}>заявки</Link> аккаунту
                        {' '}
                        {item.context.recipient?.org?.name || item.context.recipient?.name}
                    </>);
                case 'content.access.request.tv.forwarded':
                    return (<>
                        Правообладатель {item.context.additional?.sendby?.account?.org?.name || item.context.additional?.sendby?.account?.name} переадресовал <Link className="link-accent" to={`/deals/orders?id=${item.context.id}`}>заявку</Link> аккаунту
                        {' '}
                        {item.context.recipient?.org?.name || item.context.recipient?.name}
                    </>);
                case 'content.unit.send_to_user':
                    return (<>
                        Поставщик
                        {' '}
                        <Highlighter
                            highlightClassName="search__results-highlight"
                            searchWords={[highlight || '']}
                            autoEscape={true}
                            highlightTag={'span'}
                            textToHighlight={item.context.sender?.name || item.context.sender?.profile?.company}
                        />
                        {' '}
                        открыл Вам доступ на скачивание
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content?.title}
                            />
                        </Link>
                    </>);
                case 'moderation.unit_agreed':
                case 'moderation.unit.agreed':
                    return (<>
                        Модератор одобрил вашу единицу контента
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'moderation.unit_published':
                case 'moderation.unit.published':
                    return (<>
                        Поставщик
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context.content.owner.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.owner.name}
                            />
                        </Link> опубликовал единицу контента
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'moderation.unit_changed':
                case 'moderation.unit.changed':
                    return (<>
                        Поставщик
                        {' '}
                        <Highlighter
                            highlightClassName="search__results-highlight"
                            searchWords={[highlight || '']}
                            autoEscape={true}
                            highlightTag={'span'}
                            textToHighlight={item.context.content.owner.company}
                        /> изменил описание единицы контента
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'moderation.unit_filled':
                case 'moderation.unit.filled':
                    return (<>
                        Модератор заполнил вашу единицу контента
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'moderation.send_report':
                    return (<>
                        Модератор отправил отчет о единице контента
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'content.unit.new':
                    return (<>
                        Доступен новый контент
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>)
                case 'content.unit.offer':
                    return (<>
                        Получено новое предложение
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>)
                case 'user.account.activate':
                    return (<>
                        Новый пользователь с ролью
                        {' '}
                        <Link className="link-accent" to={'/requests'}>
                            «{item.context.role.ru}»
                        </Link>
                    </>)
                case 'orbox.task.failed':
                    return (<>
                        Возникла ошибка при проверке медиафайла единицы контента:
                        {' '}
                        {item.context?.content?.id &&
                            <>
                                <Link className="link-accent" to={`/content/view/${item.context.content?.id}`}>
                                    <Trans>Content</Trans>
                                </Link>
                            </>}
                        {' '}
                        <strong>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context?.orbox?.error}
                            />
                        </strong>
                    </>)
                case 'orbox.check.failed':
                    return (<>
                        Медиафайл единицы контента
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                        {' '}
                        не прошел проверку
                    </>)
                case 'content.access.permission.issued.recipient':
                case 'content.access.permission.issued.other':
                    return (<>
                        Предоставлено
                        {' '}
                        <Link className="link-accent" to={`/access/permissions/${item.context.id}`} onClick={(e) => {
                            e.preventDefault();
                            callback && callback(item.context.id);
                        }}>
                            разрешение
                        </Link>
                        {' '}
                        от <Link className="link-accent" to={`/accounts/view/${item.context.issuer?.id}`}>{item.context.issuer?.name}</Link>
                        {' '}
                        для {!isPartnerSource ? <Link className="link-accent" to={`/accounts/${item.context.recipient?.id}`}>{item.context.recipient?.name}</Link> : item.context.recipient?.name}
                        {' '}
                        на контент
                        {' '}
                        {item.context?.units?.map((u: AccessPermissionUnit) => {
                            return (<div key={u.unit.id}>
                                {(isPartnerSource && !isMedialogistika) ?
                                    `"${u.unit.title}"`
                                    :
                                    <Link className="link-accent" to={`/content/view/${u.unit.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.unit.title}
                                        />
                                    </Link>
                                }
                                {' '}
                            </div>)
                        })}
                    </>)
                case 'content.access.permission.revoked.recipient':
                case 'content.access.permission.revoked.other':
                    return (<>
                        {/* <Link className="link-accent" to={`/access/permissions/${item.context.id}`}> */}
                        Разрешение
                        {/* </Link> */}
                        {' '}
                        на контент
                        {' '}
                        {item.context?.units?.map((u: AccessPermissionUnit) => {
                            return (<div key={u.unit.id}>
                                {(isPartnerSource && !isMedialogistika) ?
                                    `"${u.unit.title}"`
                                    :
                                    <Link className="link-accent" to={`/content/view/${u.unit.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.unit.title}
                                        />
                                    </Link>}
                                {' '}
                            </div>)
                        })}
                        отозвано.
                    </>);
                case 'content.permission.letter':
                    return (<>
                        Отправлено письмо: {item.context.issuer?.org?.name}, является правообладателем указанных ниже телеканалов
                        , и не возражает против получения {item.context.recipient?.org?.name} сигналов телеканалов:
                        {' '}
                        {item.context.units.map((unitinfo: AccessPermissionUnit) =>
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            `${unitinfo.unit.title}`
                        ).join(', ')}
                    </>);
                case 'user.request.existed':
                    return (<>
                        Попытка регистрации на уже заведенный email ({item.context.email})
                    </>)
                case 'notification.message':
                    return (<>
                        {item.context.link ?
                            <Link className="link-accent" to={item.context.link}>{item.context.message}</Link>
                            :
                            item.context.message
                        }
                    </>);
                case 'product.request.new':
                    return (<>
                        Потребитель
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context.request?.sender?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.request?.sender?.name || item.context.request?.sender?.profile?.company}
                            />
                        </Link>
                        {' '}
                        запрашивает <Link className="link-accent" to={`/deals/orders?id=${item.context.request?.id}`}>оказание</Link> услуг
                        {' '}
                        { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            item.context.request.products.map((u: any) =>
                                <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={u.name?.ru || u.name}
                                    />
                                    {' '}
                                </Link>
                            )
                        }
                    </>);
                case 'product.request.granted':
                    return (
                        <>
                            Получено разрешение на получение услуг
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.ru || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                        </>
                    );
                case 'product.request.rejected':
                    return (
                        <>
                            Запрос на получение услуг
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.ru || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                            отклонен.
                        </>
                    );
                case 'product.new':
                    return (
                        <>
                            Доступна новая услуга
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.ru || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                        </>
                    );
                case 'product.offer':
                    return (
                        <>
                            Получено новое предложение услуг
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.ru || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                        </>
                    );
                case 'product.request.agreed':
                    return (
                        <>
                            Поставщик готов оказать услуги
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.ru || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                            по заявке
                        </>
                    );
                case 'content.access.permission.reminder.expire.consumer':
                    return (
                        <>
                            Разрешение на телесигналы от
                            {' '}
                            <Link className="link-accent" to={`/accounts/view/${item.context.issuer?.id}`}>{item.context.issuer?.name}</Link>
                            {' '}
                            закончится {item.context.validity.upper}.
                            {' '}
                            Запросить новое <Link className="link-accent" to="/showcase">Разрешение</Link>.
                            {' '}
                            <Link className="link-accent" to="/deals/registry">Уточнить данные</Link>
                        </>
                    );
                case 'content.access.permission.reminder.expire.provider':
                    return (
                        <>
                            Заканчивается разрешение на телесигналы от
                            {' '}
                            <Link className="link-accent" to={`/accounts/${item.context.issuer?.id}`}>{item.context.issuer?.name}</Link>
                            {' для '}
                            <Link className="link-accent" to={`/accounts/view/${item.context.recipient?.id}`}>{item.context.recipient?.name}</Link>.
                            {' '}
                            Продлить <Link className="link-accent" to={`/access/permissions/create?perm=${item.context?.id}`}>Разрешение</Link>.
                            {' '}
                            <Link className="link-accent" to="/deals/registry">Уточнить данные</Link>
                        </>
                    );
                case 'content.access.permission.reminder.expire.partner':
                    return (
                        <>
                            Для организации {item.context.recipient?.org?.name} {item.context.validity?.upper}
                            {' '}
                            заканчивается срок действия разрешения на доставку телесигналов
                            {' '}
                            от {item.context.issuer?.org?.name}.
                        </>
                    );
                case 'sdn.attr.orbox.checked':
                    return (<>
                        Медиафайл {isAdmin ? item.context.prefix : item.context.filename} прошел автоматическую проверку системой Мдиска на соответствие параметров отраслевым стандартам.
                        {' '}
                        Результат проверки: <Link
                            className="link-accent"
                            to=""
                            onClick={() => {
                                if (item.context.report) {
                                    fetchPdf({
                                        prefix: encodeURIComponent(item.context.prefix),
                                        reportType: 'attr.orbox.report',
                                        created: encodeURIComponent(item.context.checked_at),
                                    }).unwrap().then((data) => {
                                        saveAs(data, `report_${getBasename(item.context.prefix)}_${item.context.checked_at}.pdf`);
                                    }).catch((err) => {
                                        alert.error(extractErrorDetail(err));
                                    });
                                }
                            }}>
                            {item.context.passed ? 'успешно' : 'неуспешно'}
                        </Link>.
                        {' '}
                        {item.context.prefix && (
                            <Link className="link-accent" to={isAdmin ? `/sdn/avail/${encodePrefix(item.context.prefix)}` : `/sdn/my/${encodeKey(item.context.prefix)}`}>
                                Перейти к файлу
                            </Link>
                        )}
                    </>)
                case 'sdn.attr.linza.checked':
                    return (<>
                        Медиафайл {isAdmin ? item.context.prefix : item.context.filename} прошел автоматическую ИИ-проверку системой Мдиска на соблюдение законодательства РФ.
                        {' '}
                        Результат проверки: <Link
                            className="link-accent"
                            to=""
                            onClick={() => {
                                if (item.context.report) {
                                    fetchPdf({
                                        prefix: encodeURIComponent(item.context.prefix),
                                        reportType: 'attr.linza.report',
                                        created: encodeURIComponent(item.context.checked_at),
                                    }).unwrap().then((data) => {
                                        saveAs(data, `report_${getBasename(item.context.prefix)}_${item.context.checked_at}.pdf`);
                                    }).catch((err) => {
                                        alert.error(extractErrorDetail(err));
                                    });
                                }
                            }}>
                            {item.context.passed ? 'успешно' : 'неуспешно'}
                        </Link>.
                        {' '}
                        {item.context.prefix && (
                            <Link className="link-accent" to={isAdmin ? `/sdn/avail/${encodePrefix(item.context.prefix)}` : `/sdn/my/${encodeKey(item.context.prefix)}`}>
                                Перейти к файлу
                            </Link>
                        )}
                    </>)
                case 'sdn.attr.checksums.failed':
                    return (<>
                        В процессе регулярной проверки файлов на Мдиске {item.context.perm?.account?.orgname || item.context.perm?.account?.name}
                        {' '}
                        выявлено несоответствие контрольных сумм и файл {item.context.filename} был заблокирован.
                        {' '}
                        Перейти в <Link className="link-accent" to="/sdn/my">Мдиск</Link>
                    </>)
                case 'sdn.permission.added':
                    return (<>
                        Администратор {item.context.perm?.account?.orgname || item.context.perm?.account?.name} предоставил доступ к данным на Мдиске:
                        {' '}
                        {item.context.perm?.filename}
                        {' '}
                        Статус&nbsp;доступа:&nbsp;{item.context?.perm?.perm?.ru}.
                        {' '}
                        Перейти в <Link className="link-accent" to={`/sdn/avail/${encodePrefix(item.context.perm?.prefix)}`}>Мдиск</Link>
                    </>)
                case 'sdn.permission.changed':
                    return (<>
                        Администратор {item.context.perm?.account?.orgname || item.context.perm?.account?.name} изменил доступ к данным на Мдиске:
                        {' '}
                        {item.context.perm?.filename}
                        {' '}
                        Статус доступа: {item.context.perm?.perm?.ru}.
                        {' '}
                        Перейти в <Link className="link-accent" to={`/sdn/avail/${encodePrefix(item.context.perm?.prefix)}`}>Мдиск</Link>
                    </>)
                case 'sdn.permission.revoked':
                    return (<>
                        Администратор {item.context.perm?.account?.orgname || item.context.perm?.account?.name} изменил доступ к данным на Мдиске:
                        {' '}
                        {item.context.perm?.filename}
                        {' '}
                        Статус доступа: Нет доступа.
                        {' '}
                        Перейти в <Link className="link-accent" to="/sdn/avail">Мдиск</Link>
                    </>)
                case 'sdn.disk.freespace.warning':
                    return (<>
                        Заканчивается место на Мдиске {item.context.disk?.owner?.orgname}. Осталось менее {item.context.freespace_p}% от объема.
                        {' '}
                        Открыть <Link className="link-accent" to="/sdn/my">Мдиск</Link>
                    </>)
                case 'sdn.disk.blocked.write':
                    return (<>
                        Закончилось место на Мдиске {item.context.disk?.owner?.orgname} и блокирована запись на диск.
                        {' '}
                        Открыть <Link className="link-accent" to="/sdn/my">Мдиск</Link>
                    </>)
                case 'sdn.disk.unlocked.write':
                    return (<>
                        {item.context.disk?.owner?.orgname} Мдиск разблокирован на запись и готов к работе.
                        {' '}
                        Открыть <Link className="link-accent" to="/sdn/my">Мдиск</Link>
                    </>)
                case 'sdn.disk.blocked.access':
                    return (<>
                        У аккаунта {item.context.disk?.owner?.orgname} нет активного оплаченного тарифа <Link className="link-accent" to="/sdn/my">Мдиск</Link>.
                    </>)
                case 'sdn.disk.unlocked.access':
                    return (<>
                        Доступ на {item.context.disk?.owner?.orgname} Мдиск разблокирован.
                        {' '}
                        Открыть <Link className="link-accent" to="/sdn/my">Мдиск</Link>
                    </>)
                case 'sdn.object.ratelimit':
                    return (<>
                        Превышен лимит скачивания отдельного файла с Мдиска.
                        {' '}
                        {item.context.link && <>Заблокированная <Link className="link-accent" to={item.context.link}>ссылка</Link></>}
                    </>)
                case 'sdn.iam.svcacct.invite':
                    return (<>
                        Вам было выдано разрешение на создание ключей доступа в S3.
                        {' '}
                        {item.context.invite_url && <>Настроить по <a className="link-accent" target="_blank" href={item.context.invite_url}>ссылке</a></>}
                    </>)
                case 'sdn.object.ratelimit.locked':
                    return (<>
                        Превышен лимит файла с Мдиска ({item.context.prefix}).
                        {' '}
                        Лимит установлен на скачивание не более 100 раз в сутки, либо не более 30 раз за час.
                        {' '}
                        {item.context.prefix && <Link className="link-accent" to={`/sdn/avail/${encodePrefix(item.context.prefix)}`}>Перейти к файлу</Link>}.
                        {' '}
                        {item.context.link && <a href={item.context.link}>Ссылка</a>}
                    </>);
                case 'content.access.permission.warn':
                    return (<>
                        В карточке аккаунта {item.context.account?.name} организации {item.context.organization?.name} отсутствует документ,
                        {' '}
                        подтверждающий право выдачи разрешений через Медиабазу.
                        {' '}
                        Просим оформить и предоставить администратору Медиабазы необходимое основание:
                        {' '}
                        информационное письмо, документ ЭДО или иной согласованный документ, подтверждающий право выдачи разрешений.
                        <Link to="/help">Связаться с администратором</Link>
                    </>)
                default:
                    return (<>
                        {JSON.stringify(item)}
                    </>)
            }
        } catch (e) {
            console.warn(e);
            const content = JSON.stringify(item);
            return (<div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={content}>
                {content}
            </div>)
        }
    } else {
        try {
            const template = item.template as NotifificationTemplate;
            switch (template) {
                case 'content.unit.access.request.new':
                    return (<>
                        Consumer
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context.request?.sender?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.request?.sender?.name || item.context.request?.sender?.profile?.company}
                            />
                        </Link>
                        {' '}
                        requests <Link className="link-accent" to={`/deals/orders?id=${item.context.request?.id}`}>the terms of deal</Link> for
                        {' '}
                        {item.context.request.content ?
                            <Link className="link-accent" to={`/content/view/${item.context.request.content.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={item.context.request.content.title}
                                />
                            </Link>
                            :
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            item.context.request.units.map((u: any) =>
                                <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={u.title}
                                    />
                                    {' '}
                                </Link>
                            )
                        }
                    </>);
                case 'content.access.request.media.new':
                    return (<>
                        Consumer
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context?.consumer?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context?.consumer?.name}
                            />
                            {' '}
                        </Link>
                        {' '}
                        requests download access for
                        {' '}
                        {item.context?.units?.map((u: { id: number, title: string }) =>
                            <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={u.title}
                                />
                                {' '}
                            </Link>
                        )}
                    </>);
                case 'content.access.request.tv.new':
                    return (<>
                        Consumer
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context?.consumer?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context?.consumer?.name}
                            />
                        </Link>
                        {' '}
                        requests <Link className="link-accent" to={`/deals/orders?id=${item.context.id}`}>access</Link> for TV channels
                        {' '}
                        {item.context?.units?.map((u: { id: number, title: string }) =>
                            <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={u.title}
                                />
                                {' '}
                            </Link>
                        )}
                    </>);
                case 'support.message.new':
                    return (<>
                        Support message: User
                        {' '}
                        <Link className="link-accent" to={`/users/${item.context.request?.sender.user_id}`}>
                            {item.context.request?.sender?.firstname} {item.context.request?.sender?.lastname} {item.context.request?.sender?.user}
                        </Link>
                        {' ('}
                        <Link className="link-accent" to={`/accounts/${item.context.request?.sender.account_id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.request?.sender.account?.name || ''}
                            />
                        </Link>
                        {') '}
                        write
                        "{item.context.request?.message}"
                    </>);
                case 'feedback.message.new':
                    return (<>
                        A new message was received via the feedback form {item.context.feedback?.sender?.email ? `${item.context.feedback?.sender?.email}` : ''}:
                        {' '}
                        <pre>"{item.context.feedback?.message}"</pre>
                    </>);
                case 'user.request.new':
                    return (<>
                        Request for registration: <strong>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={`${item.context.profile.lastname} ${item.context.profile.firstname}`}
                            />
                        </strong> company <strong>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.profile.company}
                            />
                        </strong>
                    </>);
                case 'content.access.request.media.granted':
                    return (
                        <>
                            Granted access to download
                            {' '}
                            {item.context.units?.map((u: { id: number, title: string }) => <>
                                <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={u.title}
                                    />
                                    {' '}
                                </Link>
                                <Button size="small" onClick={() => onDownload(u.id, u.title)}>
                                    <Trans>Download</Trans>
                                </Button>
                            </>
                            )}
                        </>
                    );
                case 'content.unit.access.request.granted':
                    return (
                        <>
                            Granted access to download
                            {' '}
                            {item.context.request.content ?
                                <Link className="link-accent" to={`/content/view/${item.context.request.content.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={item.context.request.content.title}
                                    />
                                </Link>
                                :
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.units.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.title}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                            {' '}
                            {item.context.request?.prefixes?.[0] &&
                                <Link to={`/sdn/avail/${getPrefix(item.context.request?.prefixes)}`}><Button size="small">
                                    <Trans>Download</Trans>
                                </Button></Link>}
                        </>
                    );
                case 'content.unit.access.request.pending':
                    return (
                        <>
                            Request for download
                            {' '}
                            {item.context.request.content ?
                                <Link className="link-accent" to={`/content/view/${item.context.request.content.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={item.context.request.content.title}
                                    />
                                </Link>
                                :
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.units.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.title}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                            {' '}
                            is under consideration
                        </>
                    );
                case 'content.unit.access.request.rejected':
                    return (<>
                        Request for download
                        {' '}
                        {item.context.request.content ?
                            <Link className="link-accent" to={`/content/view/${item.context.request.content.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={item.context.request.content.title}
                                />
                            </Link>
                            :
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            item.context.request.units.map((u: any) =>
                                <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={u.title}
                                    />
                                    {' '}
                                </Link>
                            )
                        }
                        {' '}
                        has been rejected
                    </>);
                case 'content.access.request.media.rejected':
                    return (<>
                        Request for content
                        {' '}
                        {item.context.units.map((u: { id: number, title: string }) => (isPartnerSource && !isMedialogistika) ?
                            `${u.title}`
                            :
                            <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={u.title}
                                />
                                {' '}
                            </Link>
                        )}
                        {' '}
                        has been rejected
                    </>);
                case 'content.access.request.tv.rejected':
                    return (<>
                        Request for the following TV channels
                        {' '}
                        {item.context.units.map((u: { id: number, title: string }) =>
                            <Link key={u.id} className="link-accent" to={`/content/view/${u.id}`}>
                                <Highlighter
                                    highlightClassName="search__results-highlight"
                                    searchWords={[highlight || '']}
                                    autoEscape={true}
                                    highlightTag={'span'}
                                    textToHighlight={u.title}
                                />
                                {' '}
                            </Link>
                        )
                        }
                        {' '}
                        has been rejected
                    </>);
                case 'content.access.request.tv.copied':
                    return (<>
                        Copyright holder {item.context.additional?.sendby?.account?.org?.name || item.context.additional?.sendby?.account?.name} sent copy of <Link className="link-accent" to={`/deals/orders?id=${item.context.id}`}>order</Link> to account
                        {' '}
                        {item.context.recipient?.org?.name || item.context.recipient?.name}
                    </>);
                case 'content.access.request.tv.forwarded':
                    return (<>
                        Copyright holder {item.context.additional?.sendby?.account?.org?.name || item.context.additional?.sendby?.account?.name} forwarded <Link className="link-accent" to={`/deals/orders?id=${item.context.id}`}>order</Link> to account
                        {' '}
                        {item.context.recipient?.org?.name || item.context.recipient?.name}
                    </>);
                case 'content.unit.send_to_user':
                    return (<>
                        Consumer
                        {' '}
                        <Highlighter
                            highlightClassName="search__results-highlight"
                            searchWords={[highlight || '']}
                            autoEscape={true}
                            highlightTag={'span'}
                            textToHighlight={item.context.sender?.name || item.context.sender?.profile?.company}
                        />
                        {' '}
                        gave you access to download
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content?.title}
                            />
                        </Link>
                    </>);
                case 'moderation.unit_agreed':
                case 'moderation.unit.agreed':
                    return (<>
                        Moderator has approved your content unit
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'moderation.unit_published':
                case 'moderation.unit.published':
                    return (<>
                        Provider
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context.content.owner.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.owner.name}
                            />
                        </Link> published content unit
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'moderation.unit_changed':
                case 'moderation.unit.changed':
                    return (<>
                        Provider
                        {' '}
                        <Highlighter
                            highlightClassName="search__results-highlight"
                            searchWords={[highlight || '']}
                            autoEscape={true}
                            highlightTag={'span'}
                            textToHighlight={item.context.content.owner.company}
                        /> changed the description of the content unit
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'moderation.unit_filled':
                case 'moderation.unit.filled':
                    return (<>
                        Moderator has filled your content unit
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'moderation.send_report':
                    return (<>
                        Moderator sent report about content unit
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>);
                case 'content.unit.new':
                    return (<>
                        New content available
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>)
                case 'content.unit.offer':
                    return (<>
                        New offer received
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                    </>)
                case 'user.account.activate':
                    return (<>
                        New user with role
                        {' '}
                        <Link className="link-accent" to={'/requests'}>
                            «{item.context.role.ru}»
                        </Link>
                    </>)
                case 'orbox.task.failed':
                    return (<>
                        An error occurred while checking the content unit's media file:
                        {' '}
                        {item.context?.content?.id &&
                            <>
                                <Link className="link-accent" to={`/content/view/${item.context.content?.id}`}>
                                    <Trans>Content</Trans>
                                </Link>
                            </>}
                        {' '}
                        <strong>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context?.orbox?.error}
                            />
                        </strong>
                    </>)
                case 'orbox.check.failed':
                    return (<>
                        Mediafiles for content unit
                        {' '}
                        <Link className="link-accent" to={`/content/view/${item.context.content.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.content.title}
                            />
                        </Link>
                        {' '}
                        check failed
                    </>)
                case 'content.access.permission.issued.recipient':
                case 'content.access.permission.issued.other':
                    return (<>
                        Granted
                        {' '}
                        <Link className="link-accent" to={`/access/permissions/${item.context.id}`} onClick={(e) => {
                            e.preventDefault();
                            callback && callback(item.context.id);
                        }}>
                            permission
                        </Link>
                        {' '}
                        from <Link className="link-accent" to={`/accounts/view/${item.context.issuer?.id}`}>{item.context.issuer?.name}</Link>
                        {' '}
                        to {isPartnerSource ? item.context.recipient?.name : <Link className="link-accent" to={`/accounts/${item.context.recipient?.id}`}>{item.context.recipient?.name}</Link>}
                        {' '}
                        on content
                        {' '}
                        {item.context?.units?.map((u: AccessPermissionUnit) => {
                            return (<div key={u.unit.id}>
                                {(isPartnerSource && !isMedialogistika) ?
                                    `"${u.unit.title}"`
                                    :
                                    <Link className="link-accent" to={`/content/view/${u.unit.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.unit.title}
                                        />
                                    </Link>
                                }
                                {' '}
                            </div>)
                        })}
                    </>)
                case 'content.access.permission.revoked.recipient':
                case 'content.access.permission.revoked.other':
                    return (<>
                        {/* <Link className="link-accent" to={`/access/permissions/${item.context.id}`}> */}
                        Permission
                        {/* </Link> */}
                        {' '}
                        on content
                        {' '}
                        {item.context?.units?.map((u: AccessPermissionUnit) => {
                            return (<div key={u.unit.id}>
                                {(isPartnerSource && !isMedialogistika) ?
                                    `"${u.unit.title}"`
                                    :
                                    <Link className="link-accent" to={`/content/view/${u.unit.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.unit.title}
                                        />
                                    </Link>
                                }
                                {' '}
                            </div>)
                        })}
                        revoked.
                    </>);
                case 'content.permission.letter':
                    return (<>
                        Letter sent: {item.context.issuer?.org?.name}, is the copyright holder of the TV channels listed below,
                        and does not object to receiving {item.context.recipient?.org?.name} TV channel signals:
                        {' '}
                        {item.context.units.map((unitinfo: AccessPermissionUnit) =>
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            `${unitinfo.unit.title}`
                        ).join(', ')}
                    </>);
                case 'user.request.existed':
                    return (<>
                        Attempt to register using an already created email ({item.context.email})
                    </>)
                case 'notification.message':
                    return (<>
                        {item.context.link ?
                            <Link className="link-accent" to={item.context.link}>{item.context.message}</Link>
                            :
                            item.context.message
                        }
                    </>);
                case 'product.request.new':
                    return (<>
                        Consumer
                        {' '}
                        <Link className="link-accent" to={`/accounts/view/${item.context.request?.sender?.id}`}>
                            <Highlighter
                                highlightClassName="search__results-highlight"
                                searchWords={[highlight || '']}
                                autoEscape={true}
                                highlightTag={'span'}
                                textToHighlight={item.context.request?.sender?.name || item.context.request?.sender?.profile?.company}
                            />
                        </Link>
                        {' '}
                        has been <Link className="link-accent" to={`/deals/orders?id=${item.context.request?.id}`}>requested</Link> services
                        {' '}
                        { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            item.context.request.products.map((u: any) =>
                                <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                    <Highlighter
                                        highlightClassName="search__results-highlight"
                                        searchWords={[highlight || '']}
                                        autoEscape={true}
                                        highlightTag={'span'}
                                        textToHighlight={u.name?.en || u.name}
                                    />
                                    {' '}
                                </Link>
                            )
                        }
                    </>);
                case 'product.request.granted':
                    return (
                        <>
                            Granted permission for services
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.en || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                        </>
                    );
                case 'product.request.rejected':
                    return (
                        <>
                            Request for services
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.en || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                            has been rejected.
                        </>
                    );
                case 'product.new':
                    return (
                        <>
                            New service available
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.en || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                        </>
                    );
                case 'product.offer':
                    return (
                        <>
                            New service offer received
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.en || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                        </>
                    );
                case 'product.request.agreed':
                    return (
                        <>
                            Provider is ready to provide services
                            {' '}
                            { // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                item.context.request.products.map((u: any) =>
                                    <Link key={u.id} className="link-accent" to={`/content/service/view/${u.id}`}>
                                        <Highlighter
                                            highlightClassName="search__results-highlight"
                                            searchWords={[highlight || '']}
                                            autoEscape={true}
                                            highlightTag={'span'}
                                            textToHighlight={u.name?.en || u.name}
                                        />
                                        {' '}
                                    </Link>
                                )
                            }
                            by request
                        </>
                    );
                case 'content.access.permission.reminder.expire.consumer':
                    return (
                        <>
                            The Permission for TV signals from
                            {' '}
                            <Link className="link-accent" to={`/accounts/view/${item.context.issuer?.id}`}>{item.context.issuer?.name}</Link>
                            {' '}
                            will expire on {item.context.validity.upper}.
                            {' '}
                            <Link className="link-accent" to="/showcase">Renew</Link> the Permission.
                            {' '}
                            <Link className="link-accent" to="/deals/registry">Review the details</Link>
                        </>
                    );
                case 'content.access.permission.reminder.expire.provider':
                    return (
                        <>
                            The Permission for TV signals from
                            {' '}
                            <Link className="link-accent" to={`/accounts/${item.context.issuer?.id}`}>{item.context.issuer?.name}</Link>
                            {' for '}
                            <Link className="link-accent" to={`/accounts/view/${item.context.recipient?.id}`}>{item.context.recipient?.name}</Link>
                            {' '}
                            is about to expire.
                            {' '}
                            Renew the <Link className="link-accent" to={`/access/permissions/create?perm=${item.context?.id}`}>Permission</Link>.
                            {' '}
                            Review the details in the <Link className="link-accent" to="/deals/registry">Registry</Link>
                        </>
                    );
                case 'content.access.permission.reminder.expire.partner':
                    return (
                        <>
                            The signal delivery permit from {item.context.recipient?.org?.name} for {item.context.issuer?.org?.name} will expire on {item.context.validity?.upper}.
                        </>
                    );
                case 'sdn.attr.orbox.checked':
                    return (<>
                        Media file {isAdmin ? item.context.prefix : item.context.filename} has passed the automated check on the Mdisk for adherence to the industry standards.
                        {' '}
                        Check Result: <Link
                            className="link-accent"
                            to=""
                            onClick={() => {
                                if (item.context.report) {
                                    fetchPdf({
                                        prefix: encodeURIComponent(item.context.prefix),
                                        reportType: 'attr.orbox.report',
                                        created: encodeURIComponent(item.context.checked_at),
                                    }).unwrap().then((data) => {
                                        saveAs(data, `report_${getBasename(item.context.prefix)}_${item.context.checked_at}.pdf`);
                                    }).catch((err) => {
                                        alert.error(extractErrorDetail(err));
                                    });
                                }
                            }}>
                            {item.context.passed ? 'success' : 'fail'}
                        </Link>.
                        {' '}
                        {item.context.prefix && (
                            <Link className="link-accent" to={isAdmin ? `/sdn/avail/${encodePrefix(item.context.prefix)}` : `/sdn/my/${encodeKey(item.context.prefix)}`}>
                                To file
                            </Link>
                        )}
                    </>)
                case 'sdn.attr.linza.checked':
                    return (<>
                        Media file {isAdmin ? item.context.prefix : item.context.filename} has passed the automated AI-powered compliance check on the Mdisk for adherence to the legislation of the Russian Federation.
                        {' '}
                        Check Result: <Link
                            className="link-accent"
                            to=""
                            onClick={() => {
                                if (item.context.report) {
                                    fetchPdf({
                                        prefix: encodeURIComponent(item.context.prefix),
                                        reportType: 'attr.linza.report',
                                        created: encodeURIComponent(item.context.checked_at),
                                    }).unwrap().then((data) => {
                                        saveAs(data, `report_${getBasename(item.context.prefix)}_${item.context.checked_at}.pdf`);
                                    }).catch((err) => {
                                        alert.error(extractErrorDetail(err));
                                    });
                                }
                            }}>
                            {item.context.passed ? 'success' : 'fail'}
                        </Link>.
                        {' '}
                        {item.context.prefix && (
                            <Link className="link-accent" to={isAdmin ? `/sdn/avail/${encodePrefix(item.context.prefix)}` : `/sdn/my/${encodeKey(item.context.prefix)}`}>
                                To file
                            </Link>
                        )}
                    </>)
                case 'sdn.attr.checksums.failed':
                    return (<>
                        During regular verification of files on {item.context.perm?.account?.orgname || item.context.perm?.account?.name} MDisk
                        {' '}
                        a checksum mismatch was detected, and the file {item.context.filename} has been locked.
                        {' '}
                        To <Link className="link-accent" to="/sdn/my">MDisk</Link>
                    </>)
                case 'sdn.permission.added':
                    return (<>
                        Administrator {item.context.perm?.account?.orgname || item.context.perm?.account?.name} has granted access to data on the Mdisk:
                        {' '}
                        {item.context.perm?.filename}
                        {' '}
                        Access&nbsp;Status:&nbsp;{item.context?.perm?.perm?.en}.
                        {' '}
                        To <Link className="link-accent" to={`/sdn/avail/${encodePrefix(item.context.perm?.prefix)}`}>MDisk</Link>
                    </>)
                case 'sdn.permission.changed':
                    return (<>
                        Administrator {item.context.perm?.account?.orgname || item.context.perm?.account?.name} has changed the access permissions to data on the Mdisk:
                        {' '}
                        {item.context.perm?.filename}
                        {' '}
                        Access Status: {item.context.perm?.perm?.en}.
                        {' '}
                        To <Link className="link-accent" to={`/sdn/avail/${encodePrefix(item.context.perm?.prefix)}`}>MDisk</Link>
                    </>)
                case 'sdn.permission.revoked':
                    return (<>
                        Administrator {item.context.perm?.account?.orgname || item.context.perm?.account?.name} has changed the access permissions to data on the Mdisk:
                        {' '}
                        {item.context.perm?.filename}
                        {' '}
                        Access Status: No access.
                        {' '}
                        To <Link className="link-accent" to="/sdn/avail">MDisk</Link>
                    </>)
                case 'sdn.disk.freespace.warning':
                    return (<>
                        {item.context.disk?.owner?.orgname} Mdisk is almost full. Less than {item.context.freespace_p}% left.
                        {' '}
                        Check <Link className="link-accent" to="/sdn/my">Mdisk</Link>
                    </>)
                case 'sdn.disk.blocked.write':
                    return (<>
                        {item.context.disk?.owner?.orgname} Mdisk is full. Write access locked.
                        {' '}
                        Check <Link className="link-accent" to="/sdn/my">Mdisk</Link>
                    </>)
                case 'sdn.disk.unlocked.write':
                    return (<>
                        The account {item.context.disk?.owner?.orgname} does not have an active paid <Link className="link-accent" to="/sdn/my">Mdisk</Link> plan.
                    </>)
                case 'sdn.disk.blocked.access':
                    return (<>
                        Access to {item.context.disk?.owner?.orgname} Mdisk is locked.
                        {' '}
                        Check <Link className="link-accent" to="/sdn/my">Мдиск</Link>
                    </>)
                case 'sdn.disk.unlocked.access':
                    return (<>
                        Access to {item.context.disk?.owner?.orgname} Mdisk is unlocked.
                        {' '}
                        Check <Link className="link-accent" to="/sdn/my">Mdisk</Link>
                    </>)
                case 'sdn.object.ratelimit':
                    return (<>
                        Download limit exceeded for a single file from Mdisk.
                        {' '}
                        {item.context.link && <>Locked <Link className="link-accent" to={item.context.link}>link</Link></>}
                    </>)
                case 'sdn.iam.svcacct.invite':
                    return (<>
                        You have been granted permission to create access keys in S3.
                        {' '}
                        {item.context.invite_url && <>Set up via <a className="link-accent" target="_blank" href={item.context.invite_url}>link</a></>}
                    </>)
                case 'sdn.object.ratelimit.locked':
                    return (<>
                        Download limit exceeded for a file from Mdisk ({item.context.prefix}).
                        {' '}
                        The limit is 100 downloads per day or 30 downloads per hour.
                        {' '}
                        {item.context.prefix && <Link className="link-accent" to={`/sdn/avail/${encodePrefix(item.context.prefix)}`}>Check file</Link>}.
                        {' '}
                        {item.context.link && <a href={item.context.link}>Link</a>}
                    </>);
                case 'content.access.permission.warn':
                    return (<>
                        The profile of account {item.context.account?.name} for organization  {item.context.organization?.name} does not contain a document
                        {' '}
                        confirming the right to issue permissions through Mediabaza.
                        {' '}
                        Please prepare and provide the required basis to the Mediabaza administrator:
                        {' '}
                        an information letter, EDI document, or another agreed document confirming the right to issue permissions.
                        <Link to="/help">Contact administrator</Link>
                    </>)
                default:
                    return (<>
                        {JSON.stringify(item)}
                    </>)
            }
        } catch (e) {
            console.warn(e);
            const content = JSON.stringify(item);
            return (<div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={content}>
                {content}
            </div>)
        }
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getType(item: NotificationResponse<any> | NotificationSystem<any>) {
    const template = item.template as NotifificationTemplate;
    switch (template) {
        case 'content.unit.new':
        case 'content.unit.access.request.new':
        case 'content.unit.access.request.granted':
        case 'content.unit.send_to_user':
        case 'content.unit.access.request.pending':
        case 'content.access.request.media.new':
        case 'content.access.request.media.granted':
        case 'content.access.request.media.rejected':
        case 'content.access.request.tv.new':
        case 'content.access.request.tv.rejected':
        case 'moderation.send_report':
        case 'moderation.unit_agreed':
        case 'moderation.unit.agreed':
        case 'moderation.unit_filled':
        case 'moderation.unit.filled':
        case 'moderation.unit_changed':
        case 'moderation.unit.changed':
        case 'content.permission.letter':
        case 'notification.message':
            return NOTIFY_SEVERITY.LOW;
        case 'content.unit.access.request.rejected':
        case 'user.request.new':
        case 'content.unit.offer':
        case 'feedback.message.new':
        case 'support.message.new':
        case 'moderation.unit_published':
        case 'moderation.unit.published':
        case 'user.account.activate':
        case 'orbox.task.failed':
        case 'orbox.check.failed':
        case 'content.access.permission.issued.recipient':
        case 'content.access.permission.issued.other':
        case 'content.access.permission.revoked.recipient':
        case 'content.access.permission.revoked.other':
        case 'content.access.permission.reminder.expire.consumer':
        case 'content.access.permission.reminder.expire.provider':
        case 'content.access.permission.reminder.expire.partner':
        case 'content.access.request.tv.copied':
        case 'content.access.request.tv.forwarded':
        case 'user.request.existed':
        case 'product.request.new':
        case 'product.request.agreed':
        case 'product.request.granted':
        case 'product.request.rejected':
        case 'product.new':
        case 'product.offer':
        case 'sdn.attr.orbox.checked':
        case 'sdn.attr.linza.checked':
        case 'sdn.attr.checksums.failed':
        case 'sdn.permission.added':
        case 'sdn.permission.changed':
        case 'sdn.permission.revoked':
        case 'sdn.disk.blocked.write':
        case 'sdn.disk.unlocked.write':
        case 'sdn.disk.freespace.warning':
        case 'sdn.disk.blocked.access':
        case 'sdn.disk.unlocked.access':
        case 'sdn.object.ratelimit':
        case 'sdn.iam.svcacct.invite':
        case 'sdn.object.ratelimit.locked':
        case 'content.access.permission.warn':
            return NOTIFY_SEVERITY.HIGH;
        default:
            return NOTIFY_SEVERITY.HIGH;
    }
}

interface NotificationItemProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: NotificationResponse<any>
    highlight?: string
}

export function NotificationItem({ item, highlight }: NotificationItemProps) {
    const alert = useAlert();

    const [mediafilesRequest] = useLazyGetContentUnitMediaQuery();
    const [getMetaData] = useGetMetaDataMutation();
    const [getHeader] = useGetMediaHeaderMutation();
    const { t } = useTranslation();

    const [error, setError] = useState<string>('');
    const [errorTitle, setErrorTitle] = useState<string>('');
    const [permIdDialog, setPermIdDialog] = useState(NaN);

    const added = new Date(item.added);
    const H = added.getHours();
    const M = added.getMinutes();
    const HH = H < 10 ? '0' + H.toString() : H.toString();
    const MM = M < 10 ? '0' + M.toString() : M.toString();

    function onDownload(unitId: number, title: string) {
        mediafilesRequest(Number(unitId)).unwrap().then((data) => {
            getMetaData({ id: unitId as number }).unwrap()
                .then((metaData) => {
                    const filename = title;
                    saveAs(metaData, `${filename}.zip`);
                })
                .catch(e => {
                    setErrorTitle(t('Metadata download error'));
                    setError(extractErrorDetail(e));
                })

            if (!!data.results.mediafiles?.length) {
                data.results.mediafiles?.forEach(f => {
                    getHeader(BASE_URL.substring(0, BASE_URL.length - 1) + f.url).then((d) => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if ((d as any).error?.status === 418) {
                            alert.error(t('content:2FA_ERROR'));
                        } else {
                            window.open(BASE_URL.substring(0, BASE_URL.length - 1) + f.url);
                        }
                    }).catch((err) => {
                        if (err.status === 418) {
                            alert.error(t('content:2FA_ERROR'));
                        } else {
                            alert.error(extractErrorDetail(err));
                        }
                    });
                });
            }
        }).catch((e) => {
            setErrorTitle(t('Get mediafiles error'));
            setError(extractErrorDetail(e));
        });
    }

    return (
        <div className={'notifications__item ' + getType(item)}>
            <ErrorModal isOpen={!!error} onClose={() => setError('')} content={error} title={errorTitle} />
            <PermissionViewModal isOpen={!isNaN(permIdDialog)} permId={permIdDialog} onClose={() => setPermIdDialog(NaN)} />

            <div className="notifications__item-icon">
                <Bell />
            </div>
            <div className="notifications__item-copy" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getMessage(item, onDownload, highlight, setPermIdDialog)}
            </div>
            <p className="notifications__item-date">
                {added.toLocaleDateString()} · {`${HH}:${MM}`}
            </p>
        </div>
    );
}


function NotificationList() {
    const PAGE_SIZE = 60;
    const { t } = useTranslation();

    const [current, setCurrent] = useState(0);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const role = useRole();

    const [dateSince, setDateSince] = useState<Date | undefined>();
    const [dateTill, setDateTill] = useState<Date | undefined>();
    const [refetch, { data }] = useLazyGetNotificationsUserQuery();
    const { data: clients } = useGetAccountListQuery({});
    // const [fetchTgLink, { data: tgLink }] = useLazyGetNotificationTgBotQuery();

    const onClean = useCallback(() => {
        setCompany([]);
        setDateSince(undefined);
        setDateTill(undefined);
    }, []);

    // useEffect(() => ymHit('/notifications'), []);

    useEffect(() => {
        document.title = t('Notifications');
    }, [t]);

    const [company, setCompany] = useState<string[]>([]);
    const [availCompany, setAvailCompany] = useState<{ value: string, label: string }[]>([]);
    // const [notifyTypes, setNotifyTypes] = useState<string[]>([]);

    // const [availNotifyTypes] = useState<{ value: string, label: string }[]>([
    //     { value: NOTIFY_SEVERITY.URGENT, label: t('Urgent') },
    //     { value: NOTIFY_SEVERITY.HIGH, label: t('High') },
    //     { value: NOTIFY_SEVERITY.LOW, label: t('Low') },
    // ]);

    useEffect(() => {
        setCurrent(0);
    }, [company, dateSince, dateTill]);

    useEffect(() => {
        setIsFetched(true);
        refetch({
            offset: current,
            limit: PAGE_SIZE,
            date_after: (dateSince && toMplatBackendDate(dateSince, true)) || '',
            date_before: (dateTill && toMplatBackendDate(dateTill, true)) || '',
            search: company.join(',')
            // crdate: (dateSince && `${dateSince.getFullYear()}-${dateSince.getMonth() + 1}-${dateSince.getDate()}`) || ''
        })
            .then(() => setIsFetched(false))
            .catch(() => setIsFetched(false));
    }, [current, company, dateSince, dateTill, refetch]);

    useEffect(() => {
        setHasMore(!!data?.results.next);
    }, [data?.results]);

    const handleNavigation = useCallback(() => {
        if ((document.body.scrollHeight - window.innerHeight) - window.scrollY < 300 && !isFetched) {
            if (hasMore) {
                setCurrent(data?.results.data.length || 0);
            }
        }
    }, [data?.results.data.length, hasMore, isFetched]);

    useEffect(() => {
        window.addEventListener('scroll', handleNavigation);

        return () => {
            window.removeEventListener('scroll', handleNavigation);
        };
    }, [handleNavigation]);

    useEffect(() => {
        if (clients?.results.data) {
            setAvailCompany(clients.results.data.map((c) => ({ value: c.name, label: c.name })))
        }
        // if (Number(clients?.results.totals.all) <= Number(clients?.results.data.length)) {
        //     const uniqueCompany = new Set<string>();
        //     clients?.results.data.every(s => uniqueCompany.add(s.name));
        //     const result: { value: string, label: string }[] = [];
        //     uniqueCompany.forEach((c) => result.push({ value: c, label: c }));
        //     result.sort((a, b) => {
        //         if (a.value < b.value) {
        //             return -1;
        //         }
        //         if (a.value > b.value) {
        //             return 1;
        //         }
        //         return 0;
        //     });
        //     setAvailCompany(result);
        // }
    }, [clients]);

    const [filterVisible, setFilterVisible] = useState(false);
    const [filterCount, setFilterCount] = useState<number>(0);

    useEffect(() => {
        setFilterCount(
            company.length
            // + notifyTypes.length
            + (dateSince ? 1 : 0)
            + (dateTill ? 1 : 0)
        )
    }, [company, dateSince, dateTill]);

    return (
        <>
            <Content>
                <ContentShadow />
                <MainHeader label={t('Notifications')} icon={<Notify />} />

                {/* <div style={{ marginBottom: '10px' }}>
                    Подписаться на уведомления в <Link className="link-accent" to="" onClick={() => fetchTgLink()}>Telegram</Link>
                    <br />
                    {tgLink?.results.qrcode && (
                        <>
                            <img src={tgLink.results.qrcode} width={'200px'} />
                            <br />
                            <a className="btn _bd" href={tgLink?.results.url}>Подписаться</a>
                        </>
                    )}
                </div> */}

                <MainContentFilter>
                    <div className="main__controls">
                        <div className="main__controls-filter">
                            <div className="main__controls-filter-item">
                                <DatePicker value={dateSince} placeholder={t('component:HISTORY_SINCE')} setValue={(v) => setDateSince(v)} />
                            </div>
                            <div className="main__controls-filter-item">
                                <DatePicker value={dateTill} placeholder={t('component:HISTORY_TILL')} setValue={(v) => setDateTill(v)} />
                            </div>
                            <MultiSelect
                                className="main__controls-filter-item"
                                value={company}
                                options={availCompany}
                                setValue={(v) => setCompany(v)}
                                placeholder={isConsumer(role.label) ? t('Provider') : t('Consumer')}
                                minimized
                            />
                            {/* <MultiSelect
                                className="main__controls-filter-item"
                                value={notifyTypes}
                                options={availNotifyTypes}
                                setValue={(v) => setNotifyTypes(v)}
                                placeholder={t('Notification type')}
                                minimized
                            /> */}
                            {/* TODO load into xls */}
                            {/* <a href="#xls" className="main__types-file" target="_blank" onClick={(e) => { e.preventDefault(); }}>
                                <FileDoc />
                                <Trans>Load into xls</Trans>
                            </a> */}
                            {(filterCount > 0) && <div className="main__controls-filter-item">
                                <Button className="_bd" size="small" title={t('Clean')} onClick={onClean} style={{ lineHeight: '24px' }}>
                                    🧹
                                </Button>
                            </div>}
                        </div>
                        <div className="main__controls-filter-trigger" onClick={() => setFilterVisible(!filterVisible)}>
                            <Filter />
                            {!!filterCount && <span className="main__controls-filter-trigger-counter">{filterCount}</span>}
                        </div>
                    </div>
                </MainContentFilter>

                <div className="notifications">
                    {data?.results.data
                        // .filter(item => notifyTypes.length === 0 || notifyTypes.find(notifyType => getType(item) === notifyType))
                        // .filter(item => company.length === 0 || company.find(companyName => item.context.request?.receiver?.id === companyName || item.context.request?.sender?.id === companyName || item.context.content?.owner?.id === companyName))
                        .map(item => <NotificationItem key={item.id} item={item} />)}
                    {isFetched && <Spinner />}
                </div>

                <MobileFilter
                    filterVisible={filterVisible}
                    setFilterVisible={setFilterVisible}
                    onClean={onClean}
                >
                    <MobileDateFilterItem value={dateSince} label={t('component:HISTORY_SINCE')} placeholder={t('Notify date placeholder')} setValue={(v) => setDateSince(v)} />
                    <MobileDateFilterItem value={dateTill} label={t('component:HISTORY_TILL')} placeholder={t('Notify date placeholder')} setValue={(v) => setDateTill(v)} />
                    <MobileFilterItem label={t('Consumer')} options={availCompany} value={company} setValue={setCompany} />
                    {/* <MobileFilterItem label={t('Notification type')} options={availNotifyTypes} value={notifyTypes} setValue={setNotifyTypes} /> */}
                </MobileFilter>
            </Content>
        </>
    );
}

export default NotificationList;
